import { useState, useRef, useCallback, useEffect } from 'react';

export const useSmartDropdownEvent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return { top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Dimensions estimées du dropdown
    const dropdownWidth = 350;
    const dropdownHeight = 200; // Hauteur max estimée

    let top = triggerRect.bottom + 8;
    let left = triggerRect.right - dropdownWidth;

    // Vérifier le débordement en bas
    if (triggerRect.bottom + dropdownHeight > viewportHeight - 20) {
      // Pas assez d'espace en bas, afficher en haut
      top = triggerRect.top - dropdownHeight - 8;
    }

    // Vérifier le débordement à droite
    if (left + dropdownWidth > viewportWidth - 20) {
      left = viewportWidth - dropdownWidth - 20;
    }

    // Vérifier le débordement à gauche
    if (left < 20) {
      left = 20;
    }

    // Assurer que le dropdown reste dans la vue
    top = Math.max(20, Math.min(top, viewportHeight - dropdownHeight - 20));

    return { top, left };
  }, []);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    // Petit délai pour permettre le rendu avant le calcul
    setTimeout(() => {
      setPosition(calculatePosition());
    }, 10);
  }, [calculatePosition]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleDropdown = useCallback(() => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [isOpen, openDropdown, closeDropdown]);

  // Gérer le clic en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Empêcher le scroll du body quand le dropdown est ouvert
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeDropdown]);

  // Recalculer la position quand la fenêtre est redimensionnée
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        setPosition(calculatePosition());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, calculatePosition]);

  return {
    isOpen,
    position,
    triggerRef,
    dropdownRef,
    openDropdown,
    closeDropdown,
    toggleDropdown,
    setIsOpen
  };
};